import React, { useEffect, useRef } from 'react';
import parse from 'style-to-object';
import './new-card-input-button.css';
import {
	getCanvasEntityNewInputButtonDOMId,
	highlightElementIndefinetly,
	highlightElementSuccess,
	removeHighlightElementIndefinetly,
	removeHighlightElementSuccess,
} from '../../../services/editor/ui';
import { CanvasEntityElement } from '../../../services/editor/CanvasElement';
import {
	DraggableExecutableEntityState,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
} from '@elyx-code/project-logic-tree';
import { ConnectionNodeType, EditorService } from '../../../services/editor';

interface Props {
	onClick?: (event: React.UIEvent) => any;
	canvasElement: CanvasEntityElement;
	entity: DraggableExecutableEntityState;
	project: EditorService;
	disabled?: boolean;
	size?: 'small' | 'medium' | 'large';
	iconStart?: string;
	iconEnd?: string;
	parentDisabledReasons: CanvasEntityDisabledReason[];
	[key: string]: any;
}

const NewCardInputButton: React.FC<Props> = ({
	onClick,
	disabled,
	color,
	size,
	iconStart,
	iconEnd,
	parentDisabledReasons,
	project,
	entity,
	canvasElement,
	...otherProps
}) => {
	const buttonRef = useRef<HTMLButtonElement>(null);

	const {
		classes: rootClasses = [],
		style = '',
		...remainingProps
	} = otherProps || {};

	const customColor = ![
		'primary',
		'secondary',
		'error',
		'warning',
		'success',
		'grey',
	].includes(color)
		? color
		: undefined;

	const stylesString = style + (customColor ? `color: ${customColor};` : '');

	const parentOutOfScope = parentDisabledReasons.includes(
		CanvasEntityConnectionDisabledReason.NotInScope
	);

	useEffect(() => {
		const handleConnectionStart = (payload: any) => {
			if (
				!parentOutOfScope &&
				[ConnectionNodeType.PropertyOut].includes(payload.nodeType)
			) {
				if (buttonRef.current) {
					highlightElementIndefinetly(buttonRef.current);
				}
			}
		};

		const handleConnectionEnd = () => {
			if (buttonRef.current) {
				removeHighlightElementIndefinetly(buttonRef.current);
				removeHighlightElementSuccess(buttonRef.current);
			}
		};

		project.on('canvas-connection-start', handleConnectionStart);
		project.on('canvas-connection-end', handleConnectionEnd);

		return () => {
			project.off('canvas-connection-start', handleConnectionStart);
			project.off('canvas-connection-end', handleConnectionEnd);

			// Also clean up any lingering highlights just in case
			if (buttonRef.current) {
				removeHighlightElementIndefinetly(buttonRef.current);
				removeHighlightElementSuccess(buttonRef.current);
			}
		};
	}, [
		project,
		parentOutOfScope,
		buttonRef,
		highlightElementIndefinetly,
		removeHighlightElementIndefinetly,
		removeHighlightElementSuccess,
	]);

	return (
		<button
			ref={buttonRef}
			className={[
				'new-card-input-button',
				...(!customColor
					? [`new-card-input-button__${color ?? 'primary'}`]
					: []),
				...rootClasses,
			].join(' ')}
			id={getCanvasEntityNewInputButtonDOMId(entity)}
			style={parse(stylesString) || undefined}
			onMouseOver={(e: any) => {
				if (!!project.canvas?.activeConnectionDrawing && !parentOutOfScope) {
					if (
						project.onGoingConnectionSource &&
						[ConnectionNodeType.PropertyOut].includes(
							project.onGoingConnectionNodeType
						)
					) {
						project.onGoingConnectionTarget = entity;
						project.onGoingConnectionTargetId =
							getCanvasEntityNewInputButtonDOMId(entity);

						project.canvas?.activeConnectionDrawing.snap(
							getCanvasEntityNewInputButtonDOMId(entity)
						);

						removeHighlightElementIndefinetly(buttonRef.current);
						highlightElementSuccess(buttonRef.current);
					}
				}

				remainingProps.onMouseOver?.(e);
			}}
			onMouseLeave={(e: any) => {
				if (
					!!project.canvas?.activeConnectionDrawing &&
					!parentOutOfScope &&
					[ConnectionNodeType.PropertyOut].includes(
						project.onGoingConnectionNodeType
					)
				) {
					removeHighlightElementIndefinetly(buttonRef.current);
					highlightElementIndefinetly(buttonRef.current);

					if (
						project.canvas?.activeConnectionDrawing?.snappedTo ===
						getCanvasEntityNewInputButtonDOMId(entity)
					) {
						project.canvas?.activeConnectionDrawing?.unSnap();
					}

					if (
						project.onGoingConnectionTarget === entity &&
						project.onGoingConnectionTargetId ===
							getCanvasEntityNewInputButtonDOMId(entity) &&
						[ConnectionNodeType.PropertyOut].includes(
							project.onGoingConnectionNodeType
						)
					) {
						project.onGoingConnectionTarget = null;
						project.onGoingConnectionTargetId = null;
					}
				}

				remainingProps.onMouseLeave?.(e);
			}}
			onClick={onClick}
		>
			<div className='new-card-input-button__background'></div>
			<div className='new-card-input-button__content'>
				<div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
					{iconStart && <i className={`${iconStart}`}></i>}
					{otherProps.children}
					{iconEnd && <i className={`${iconEnd}`}></i>}
				</div>
			</div>
		</button>
	);
};

export default NewCardInputButton;
